import { useState } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Typography,
  Button,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { auth } from 'services/firebase/client';
import Logo from 'assets/icons/logo_transparente_preto.png';
import { cssStyles, sxStyles } from './styles';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const validateEmail = (emailToValidate: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailToValidate);
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    if (!email) {
      setError('Por favor, insira um e-mail.');
      return;
    }

    if (!validateEmail(email)) {
      setError('Por favor, insira um e-mail válido.');
      return;
    }

    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess(
        'E-mail de redefinição enviado! Verifique sua caixa de entrada e também a pasta de spam.',
      );
      setEmail(''); // Limpa o campo após sucesso
    } catch (err: any) {
      console.error('Erro ao redefinir senha:', err);

      // Tratamento de erros mais específico
      if (err.code === 'auth/user-not-found') {
        setError('E-mail não encontrado. Verifique se o e-mail está correto.');
      } else if (err.code === 'auth/invalid-email') {
        setError('E-mail inválido. Por favor, verifique o formato.');
      } else if (err.code === 'auth/too-many-requests') {
        setError(
          'Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.',
        );
      } else {
        setError('Erro ao enviar e-mail. Tente novamente mais tarde.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleSubmit();
    }
  };

  return (
    <Box sx={sxStyles.mainContainer}>
      <Card sx={sxStyles.card}>
        <CardContent>
          {/* Logo */}
          <Box sx={{ textAlign: 'center' }}>
            <img
              src={Logo}
              alt="Logo Clube da 42"
              style={cssStyles.logoStyle}
            />
          </Box>

          {/* Título */}
          <Typography variant="h5" sx={sxStyles.welcomeText}>
            Esqueceu sua senha?
          </Typography>
          <Typography variant="body2" sx={{ textAlign: 'center', mb: 2 }}>
            Insira seu e-mail abaixo para receber um link de redefinição de
            senha.
          </Typography>

          {/* Alertas */}
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          {/* Formulário */}
          <TextField
            fullWidth
            label="E-mail"
            type="email"
            variant="outlined"
            margin="normal"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading || !!success}
            error={!!error}
            helperText={error || ''}
          />
          <Button
            fullWidth
            variant="contained"
            sx={{ mt: 2 }}
            onClick={handleSubmit}
            disabled={loading || !!success}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Enviar link de recuperação'
            )}
          </Button>

          {/* Links para login */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Link
              component={RouterLink}
              to="/login"
              underline="hover"
              variant="body2"
              sx={sxStyles.link}
            >
              Voltar para login
            </Link>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ForgotPassword;
